import { useMemo, useEffect, useState, useCallback } from "react";
import { CheckboxGroup, Col, Form, FormInstance, Row, Select, useToast, Icon, Icons, SelectOption, Spin } from "@ir/ui";
import {
  DisputeDataForSubmit,
  DisputeDocument,
  RaiseDisputeDotfoodsFormValues,
  RaiseDisputeModalProps,
} from "./RaiseDisputeInterface";
import { DisputeType, IsDocumentsRequiredConfig } from "@/core/repositories/deduction";
import { GetDisputeConfigDTO } from "@/core/dto";
import { DeductionDisputeRepository } from "@/infrastructure/repositories/deduction/DeductionDispute.repository";
import { GetDisputeConfigUseCase } from "@/core/usecases/deduction";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { useUser } from "@/hooks";
import ContactInfo from "../../../ContactInfoForm";
import DotfoodsFormContent from "./utils/DotfoodsFormContent";
import DotfoodsPrmFormContent from "./utils/DotfoodsPrmFormContent";
import { createLogger } from "@/infrastructure/services/logger";
import DisputeNotesField from "./DisputeNotesField";

const client = GraphQlAdapter.getInstance().getClient();

interface RiseDisputeDotfoodsFormProps extends RaiseDisputeModalProps {
  disputeForm: FormInstance<RaiseDisputeDotfoodsFormValues>;
  onDisputeSubmit: (disputeData: DisputeDataForSubmit) => void;
  deductionInvoiceAmount: number;
  remittanceItemNetAmount: number | null;
  documents: DisputeDocument[];
}

const RiseDisputeDotfoodsForm = ({
  deduction,
  documents,
  disputeForm,
  onDisputeSubmit,
  remittanceItemNetAmount,
  deductionInvoiceAmount,
  checkNumber,
  claimNumber,
}: RiseDisputeDotfoodsFormProps) => {
  const logger = useCallback(() => createLogger("RaiseDisputeDotfoodsForm"), []);
  const [disputeTypes, setDisputeTypes] = useState<DisputeType[]>([]);
  const [disputeTypeChildren, setDisputeTypeChildren] = useState<{ label: string; value: string }[]>([]);
  const [disputeContactInfoList, setDisputeContactInfoList] = useState<{ label: string; value: string }[]>([]);
  const [isDisputeTypesLoading, setIsDisputeTypesLoading] = useState(false);
  const [isCustomerDeduction, setIsCustomerDeduction] = useState(false);
  const [portalKey, setPortalKey] = useState<string | null>(null);
  const [isDocumentsRequiredConfig, setIsDocumentsRequiredConfig] = useState<IsDocumentsRequiredConfig[]>([]);
  const [dataProviderType, setDataProviderType] = useState<string | null>(null);
  const [noteTemplate, setNoteTemplate] = useState<string | null>(null);
  const { user } = useUser();
  const { error: errorToast } = useToast();

  // full form dom loading state
  const [isDotFoodsFormLoading, setIsDotFoodsFormLoading] = useState(true);

  // Issue Categories - Parent dispute types
  const getIssueCategories = (): SelectOption[] => {
    let issueCategories: DisputeType[] = [];
    if (!disputeTypes || !Array.isArray(disputeTypes)) {
      return [];
    }

    if (isCustomerDeduction) {
      issueCategories = disputeTypes.filter(category => category.isCustomerDeduction);
    } else {
      issueCategories = disputeTypes.filter(category => !category.isCustomerDeduction);
    }

    return issueCategories.map(category => ({
      label: category.disputeTypeName,
      value: category.disputeTypeId.toString(),
    }));
  };

  const isDocumentsRequired = useMemo(() => {
    return (
      isDocumentsRequiredConfig.find(config => config.deductionTypeId === deduction.deductionTypeId)
        ?.isDocumentsRequired ?? false
    );
  }, [isDocumentsRequiredConfig, deduction.deductionTypeId]);

  // check the claim number is mock invoice
  useEffect(() => {
    // If claim number start with 'DE-' or 'DEE-' or 'AO-' or 'AOO-'
    if (
      claimNumber?.startsWith("DE-") ||
      claimNumber?.startsWith("DEE-") ||
      claimNumber?.startsWith("AO-") ||
      claimNumber?.startsWith("AOO-")
    ) {
      setIsCustomerDeduction(true);
    } else {
      setIsCustomerDeduction(false);
    }
  }, [claimNumber]);

  // Issue Descriptions - Children of selected category
  const getIssueDescriptions = (categoryId: number | null): SelectOption[] => {
    if (!categoryId || !disputeTypes || !Array.isArray(disputeTypes)) {
      return [];
    }
    const category = disputeTypes.find(cat => cat.disputeTypeId === categoryId);
    return (
      category?.children?.map(child => ({
        label: child.disputeTypeName,
        value: child.disputeTypeId.toString(),
      })) ?? []
    );
  };

  // Documents
  const getDocuments = (): SelectOption[] => {
    const compareDocuments = (a: DisputeDocument, b: DisputeDocument) => {
      if (a.deletable && !b.deletable) return 1;
      if (!a.deletable && b.deletable) return -1;
      return 0;
    };
    return [...documents].sort(compareDocuments).map(document => ({
      label: document.name,
      value: document.id.toString(),
    }));
  };

  useEffect(() => {
    const fetchDisputeTypes = async () => {
      try {
        setIsDisputeTypesLoading(true);
        const response = await new GetDisputeConfigUseCase(new DeductionDisputeRepository(client)).execute(
          new GetDisputeConfigDTO({
            purchaserId: deduction?.purchaserId,
          })
        );
        setDisputeTypes(response.disputeConfig.disputeTypes);
        setIsDocumentsRequiredConfig(response.disputeConfig.isDocumentsRequiredConfig);
        setPortalKey(response.disputeConfig.portalKey);
        setDataProviderType(response.disputeConfig?.dataProviderType);

        setNoteTemplate(response.disputeConfig.noteTemplate);
        disputeForm.setFieldsValue({
          disputeRequestMessage: response.disputeConfig.noteTemplate ?? "",
        });

        if (response.disputeConfig?.disputeContacts?.length > 0) {
          setDisputeContactInfoList(
            response.disputeConfig.disputeContacts.map(contact => ({
              label: `${contact.name} - ${contact.email}`,
              value: contact.email,
            }))
          );

          if (response.disputeConfig?.disputeContacts?.length === 1) {
            const contactInfo = response.disputeConfig.disputeContacts[0];
            disputeForm.setFieldValue("contactName", contactInfo.name);
            disputeForm.setFieldValue("contactEmail", contactInfo.email);
            disputeForm.setFieldValue("contactInfo", {
              label: `${contactInfo.name} - ${contactInfo.email}`,
              value: contactInfo.email,
            });
          }
        }
      } catch (error) {
        logger().error("Error fetching dispute types", error);
        errorToast({
          content: "Failed to fetch dispute types",
          icon: <Icon name={Icons.AlertCircle} />,
        });
      } finally {
        setIsDisputeTypesLoading(false);
        setIsDotFoodsFormLoading(false);
      }
    };
    fetchDisputeTypes();
  }, [disputeForm, errorToast, deduction?.purchaserId, logger]);

  useEffect(() => {
    if (disputeTypes?.length > 0) {
      const findDisputeTypeByDeductionValidityRcaIdsDotfoods = disputeTypes.find(type =>
        type.deductionValidityRcaIds?.some(id => deduction?.rcaIds?.includes(id))
      );
      const findDisputeTypeByDeductionSubTypeIdDotfoods = disputeTypes.find(
        type =>
          type.deductionTypeIds?.includes(deduction?.deductionSubTypeId) ||
          type.deductionTypeIds?.includes(deduction?.deductionTypeId)
      );

      if (findDisputeTypeByDeductionValidityRcaIdsDotfoods) {
        disputeForm.setFieldValue("disputeType", {
          label: findDisputeTypeByDeductionValidityRcaIdsDotfoods.disputeTypeName,
          value: findDisputeTypeByDeductionValidityRcaIdsDotfoods.disputeTypeId?.toString(),
        });
      } else if (findDisputeTypeByDeductionSubTypeIdDotfoods) {
        disputeForm.setFieldValue("disputeType", {
          label: findDisputeTypeByDeductionSubTypeIdDotfoods.disputeTypeName,
          value: findDisputeTypeByDeductionSubTypeIdDotfoods.disputeTypeId?.toString(),
        });
      }
    }

    // Set documents
    if (documents?.length > 0) {
      const sortedDocuments = [...documents];
      disputeForm.setFieldValue(
        "documentsIds",
        sortedDocuments.filter(document => !document.deletable).map(document => document.id.toString())
      );
    }

    // Set static contact information
    if (disputeContactInfoList.length === 0 && user?.email) {
      disputeForm.setFieldValue("contactName", user.fullName);
      disputeForm.setFieldValue("contactEmail", user.email);
    }

    // set default total amount
    disputeForm.setFieldValue("totalAmount", remittanceItemNetAmount);
    // srt static dispute amount
    disputeForm.setFieldValue("disputeAmount", deductionInvoiceAmount);
  }, [
    documents,
    remittanceItemNetAmount,
    deductionInvoiceAmount,
    deduction?.purchaserId,
    disputeContactInfoList,
    user,
    disputeForm,
    disputeTypes,
    deduction?.rcaIds,
    deduction?.deductionTypeId,
    deduction?.deductionSubTypeId,
  ]);

  const handleDisputeTypeChange = () => {
    const currentDisputeType = disputeForm.getFieldValue("disputeType");
    if (!currentDisputeType) {
      return;
    }

    let issueDescriptionList: SelectOption[] = [];
    if (currentDisputeType) {
      issueDescriptionList = getIssueDescriptions(Number(currentDisputeType.value));
    }

    setDisputeTypeChildren(issueDescriptionList);

    if (issueDescriptionList.length === 1) {
      disputeForm.setFieldValue("disputeSubType", {
        label: issueDescriptionList[0].label,
        value: issueDescriptionList[0].value,
      });
    } else if (issueDescriptionList?.length > 1) {
      disputeForm.setFieldValue("disputeSubType", "");
    }
  };

  const handleSubmit = (values: RaiseDisputeDotfoodsFormValues) => {
    if (!portalKey) {
      return;
    }
    const disputeData: DisputeDataForSubmit = {
      disputeTypeId: Number(values.disputeType.value),
      disputeSubTypeId: Number(values.disputeSubType?.value),
      disputeAmount: Number(values.disputeAmount),
      totalAmount: Number(values?.totalAmount ?? remittanceItemNetAmount),
      disputeRequestMessage: values.disputeRequestMessage.toString().trim(),
      portalKey: portalKey,
      contactInfo:
        disputeContactInfoList.length > 0
          ? // if contact info is in the list
            {
              name: values.contactInfo.label.split("-")[0].trim(),
              email: values.contactInfo.value.trim(),
            }
          : // if contact info is not in the list
            {
              name: values.contactName,
              email: values.contactEmail,
            },
      documentIds: values.documentsIds?.map(Number),
      dataProviderType: dataProviderType ?? undefined,
    };
    onDisputeSubmit(disputeData);
  };

  const handleValuesChange = (changedValues: any) => {
    if (changedValues.disputeType) {
      handleDisputeTypeChange();
    }
  };

  // get form content by - portal key( DOT_FOODS / DOT_FOODS_PRM )
  const getFormContent = () => {
    if (portalKey?.toUpperCase() === "DOT_FOODS") {
      return (
        <DotfoodsFormContent
          getIssueCategories={getIssueCategories}
          isDisputeTypesLoading={isDisputeTypesLoading}
          disputeTypeChildren={disputeTypeChildren}
          isCustomerDeduction={isCustomerDeduction}
        />
      );
    } else if (portalKey?.toUpperCase() === "DOT_FOODS_PRM") {
      return (
        <DotfoodsPrmFormContent getIssueCategories={getIssueCategories} isDisputeTypesLoading={isDisputeTypesLoading} />
      );
    }
  };

  return isDotFoodsFormLoading ? (
    <Spin size="large" spinning={isDotFoodsFormLoading} />
  ) : (
    <Form form={disputeForm} onFinish={handleSubmit} onValuesChange={handleValuesChange}>
      {getFormContent()}

      <DisputeNotesField
        form={disputeForm}
        deduction={deduction}
        checkNumber={checkNumber}
        invoiceNumber={claimNumber}
        noteTemplate={noteTemplate}
      />

      {disputeContactInfoList.length > 0 && (
        <Row>
          <Col span={12}>
            <Form.Item
              name="contactInfo"
              rules={[{ required: true, message: "Contact Info is required." }]}
              label="Contact Info"
              onMetaChange={() => {
                const currentContactInfo = disputeForm.getFieldValue("contactInfo");

                if (currentContactInfo) {
                  disputeForm.setFieldValue("contactName", currentContactInfo.label);
                  disputeForm.setFieldValue("contactEmail", currentContactInfo.value);
                }
              }}
            >
              <Select
                maxMenuHeight={150}
                className="select-field"
                placeholder="Select"
                options={disputeContactInfoList}
                isClearable={false}
                isLoading={isDisputeTypesLoading}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {!isDisputeTypesLoading && disputeContactInfoList.length === 0 && (
        <ContactInfo nameLabel="Contact Name" emailLabel="Contact Email" />
      )}
      {isDocumentsRequired && (
        <Form.Item
          name="documentsIds"
          rules={[{ required: true, message: `Select at least one document.` }]}
          label="Document Selection"
        >
          <CheckboxGroup
            options={getDocuments()}
            size="medium"
            variant="group"
            color="primary"
            defaultValue={documents.filter(document => !document.deletable).map(document => document.id.toString())}
          />
        </Form.Item>
      )}
    </Form>
  );
};

export default RiseDisputeDotfoodsForm;
